import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import { getListItem } from '../../services/Item';
import { StatusField } from './StatusField';
import { useDispatch } from 'react-redux';
import Edit from '@mui/icons-material/Edit';
import { ItemEdit } from './ItemEdit';
import Add from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { ItemCreate } from './ItemCreate';
import { CsvBuilder } from 'filefy';
import Color from '../layout/color/Color';
import FontSize from '../layout/FontSize';


const saveButton = {
  bgcolor: Color.Main,
  borderRadius: '30px',
  border: '1px solid',
  fontWeight: '500',
  textAlign: 'center',
  padding: '4px 8px',
  color: Color.Button,
  transition: '0.3s',
  fontSize: FontSize.Medium,
  textTransform: 'none',
  '&:hover': { background: Color.Button, color: Color.Normal }
};

const backButton = {
  bgcolor: Color.Button,
  borderRadius: '30px',
  border: "1px solid #055C2D",
  color: Color.Main,
  fontWeight: '500',
  textAlign: 'center',
  padding: '4px 8px',
  transition: '0.3s',
  fontSize: FontSize.Medium,
  textTransform: 'none',
  '&:hover': { background: Color.Main, color: Color.Button }
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'name',
    disablePadding: false,
    label: 'Name',
  },
  {
    id: 'description',
    disablePadding: false,
    label: 'Description',
  },
  {
    id: 'price',
    disablePadding: false,
    label: 'Price',
  },
  {
    id: 'quantity',
    disablePadding: false,
    label: 'Quantity',
  },
  {
    id: 'unit',
    disablePadding: false,
    label: 'Unit',
  },
  {
    id: 'status',
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'button',
    disablePadding: false,
    label: '',
  },
];

const headCellExports = [
  {
    label: 'Name',
    id: 'name',
    disablePadding: false,
  },
  {
    label: 'Description',
    id: 'description',
    disablePadding: false,
  },
  {
    label: 'Price',
    id: 'price',
    disablePadding: false,
  },
  {
    label: 'Quantity',
    id: 'quantity',
    disablePadding: false,
  },
  {
    label: 'Unit',
    id: 'unit',
    disablePadding: false,
  },
  {
    label: 'Status',
    id: 'status',
    disablePadding: false,
  },
];

function BasicTable(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

BasicTable.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export const ItemList = (props) => {
  const [component, setComponent] = useState('LIST');
  const [editItem, setEditItem] = useState();
  const theme = useTheme();
  const dispatch = useDispatch();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [reload, setReload] = useState(false);
  const [keyword, setKeyword] = useState('');
  useEffect(() => {
    getListItem(page, rowsPerPage, keyword, parseInt(props.id))
      .then((res) => {
        setCount(res.total.count);
        setItems(res.items);
        setReload(false);
      })
      .catch((err) => {
        return err;
      });
  }, [page, rowsPerPage, keyword, props, dispatch, reload]);
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSetEdit = (row) => {
    setEditItem(row);
    setComponent('EDIT');
  };
  const handleSetList = (page) => {
    setComponent('LIST');
    setPage(page);
    setReload(true);
  };

  const handleSetCreate = () => {
    setComponent('CREATE');
  };
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - count) : 0;

  const handleExportCSV = (e) => {
    const rows = items.map((item) => {
      return headCellExports.map((headCell) => item[headCell.id]);
    });
    e.preventDefault();
    new CsvBuilder('items.csv')
      .setColumns(headCellExports.map((headCell) => headCell.label))
      .addRows(rows)
      .exportFile();
  };
  switch (component) {
    case 'LIST':
      return (
        <Grid container>
          <Grid item xs={12}>
            <Button
              endIcon={<DescriptionIcon sx={{ color: Color.Main }} />}
              sx={{ textAlign: 'left', ml: 1, cursor: 'default' }}
              size="medium"
            >
              <Typography variant="h3">Item Management</Typography>
            </Button>
          </Grid>
          <Divider sx={{ width: '100%', mb: 2 }} />
          <Grid item container xs={12}>
            <Grid xs={6} md={6} lg={6} xl={6} item>
              <TextField
                label="Search by name"
                sx={{
                  minWidth: 120,
                  width: 197,
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
                onChange={(e) => setKeyword(e.target.value)}
              ></TextField>
            </Grid>
            <Grid
              item
              xs={6}
              md={6}
              lg={6}
              xl={6}
              sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
            >
              <Button
                variant="text"
                startIcon={<Add />}
                onClick={handleSetCreate}
                sx={saveButton}
              >
                Create New
              </Button>
              <Button
                variant="text"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={backButton}
              >
                Export
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ width: '100%', mb: 2 }}>
              <TableContainer>
                <Table aria-labelledby="tableTitle" size="medium">
                  <BasicTable
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    rowCount={count}
                  />
                  <TableBody>
                    {items.length > 0
                      ? stableSort(items, getComparator(order, orderBy))?.map(
                        (row, index) => {
                          const labelId = `enhanced-table-checkbox-${index}`;
                          return (
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer' }}
                              onDoubleClick={(e) => { }}
                              key={row.id}
                            >
                              <TableCell></TableCell>
                              {headCells.map((p, index) => {
                                return p.id === 'status' ? (
                                  <TableCell
                                    component="th"
                                    id={labelId}
                                    scope="row"
                                    padding="none"
                                    key={p.id}
                                  >
                                    <StatusField status={row[p.id]} />
                                  </TableCell>
                                ) : p.id === 'button' ? (
                                  <TableCell key={p.id}>
                                    <Button
                                      startIcon={
                                        <Edit
                                          sx={{ width: 18, height: 18 }}
                                        />
                                      }
                                      sx={{ fontSize: 13 }}
                                      onClick={() => {
                                        handleSetEdit(row);
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  </TableCell>
                                ) : (
                                  <TableCell key={p.id}>
                                    {row[p.id]}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        }
                      )
                      : null}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows,
                        }}
                      >
                        <TableCell colSpan={12} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={matchDownSM ? [] : [5, 10, 25]}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Grid>
        </Grid>
      );
    case 'EDIT':
      return (
        <ItemEdit
          handleBack={() => handleSetList(page)}
          handleBackFirstPage={() => handleSetList(0)}
          item={editItem}
        />
      );
    case 'CREATE':
      return (
        <ItemCreate
          handleBack={() => handleSetList(0)}
          categoryId={parseInt(props.id)}
        />
      );
    default:
      return null;
  }
};
